import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, message, Input, Grid, Space, Flex, Card, Tooltip, Popover, Segmented } from 'antd';
import { PlusOutlined, SearchOutlined, EllipsisOutlined, AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import TicketDetailModal from './TicketDetail/TicketDetail';
import debounce from 'lodash/debounce';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { getTickets, createTicket, updateTicket, deleteTicket } from './TicketServices';
import { getChannels } from '../Channels/ChannelsServices';
import ContactsServices from '../Contacts/ContactsServices';
import KanbanBoard from './KanbanBoard/KanbanBoard';
import TicketTableView from './TicketTableView/TicketTableView';
import CreateTicketModal from './CreateTicketModal/CreateTicketModal';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { useTranslation } from 'react-i18next';
import useSocket from '../../Hooks/useSocket';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import { Resource, Action } from '../../Constants/socket';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { ticketFilterFields } from '../Shared/Filters/filterConfig';
import ExportButton from '../DataExportImport/ExportButton';
import ImportButton from '../DataExportImport/ImportButton';
import './Tickets.css';

const { useBreakpoint } = Grid;

const VIEW_STORAGE_KEY = 'ticket_view_preference';

const TicketsPage = () => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { id: routeTicketId } = useParams();
    const navigate = useNavigate();
    const [viewTicketId, setViewTicketId] = useState(routeTicketId);

    useEffect(() => {
        setViewTicketId(routeTicketId);
    }, [routeTicketId]);

    const handleCloseTicketModal = useCallback(() => {
        setViewTicketId(null);
        navigate('/tickets');
    }, [navigate]);
    const { setLoading, user: { id: currentUserId } = {} } = useGlobal();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { handleError } = useErrorHandler();
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [listFilters, setListFilters] = useState([
        { id: 'default-status', fieldKey: 'status', value: ['open', 'in_progress'] }
    ]);
    const [normalizedListFilters, setNormalizedListFilters] = useState({ status: ['open', 'in_progress'] });
    
    const [boardFilters, setBoardFilters] = useState([]);
    const [normalizedBoardFilters, setNormalizedBoardFilters] = useState({});
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
    const [channelOptions, setChannelOptions] = useState([]);
    const [contactOptions, setContactOptions] = useState([]);

    const fetchChannels = useCallback(async (search = '') => {
        try {
            const response = await getChannels({ page: 1, size: 100, search }); 
            const options = response.data.map(c => ({
                label: c.name || c.external_channel_id || c.id,
                value: c.id
            }));
            setChannelOptions(options);
        } catch (error) {
            handleError(error);
        }
    }, [handleError]);

    const debouncedFetchChannels = useMemo(
        () => debounce(fetchChannels, 800),
        [fetchChannels]
    );

    const fetchContacts = useCallback(async (search = '') => {
        try {
            const response = await ContactsServices.getContacts({ page: 1, size: 100, search });
            const options = response.data.map(contact => ({
                label: contact.full_name ||
                    [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
                    contact.external_contact_id ||
                    contact.email ||
                    contact.phone ||
                    contact.id,
                value: contact.id
            }));
            setContactOptions(options);
        } catch (error) {
            handleError(error);
        }
    }, [handleError]);

    const debouncedFetchContacts = useMemo(
        () => debounce(fetchContacts, 800),
        [fetchContacts]
    );

    useEffect(() => {
        fetchChannels();
        fetchContacts();
    }, [fetchChannels, fetchContacts]);

    const filterFields = useMemo(() => {
        return ticketFilterFields.map(field => {
            if (field.key === 'channel_id') {
                return { 
                    ...field, 
                    options: channelOptions,
                    showSearch: true,
                    onSearch: debouncedFetchChannels,
                    filterOption: false 
                };
            }
            if (field.key === 'contact_id') {
                return {
                    ...field,
                    type: 'enum',
                    options: contactOptions,
                    showSearch: true,
                    onSearch: debouncedFetchContacts,
                    filterOption: false
                };
            }
            return field;
        });
    }, [channelOptions, contactOptions, debouncedFetchChannels, debouncedFetchContacts]);
    const [refreshKey, setRefreshKey] = useState(0); // Used to force children to refetch
    const isSubscribed = useSocketSubscription(Resource.USER, currentUserId ? [currentUserId] : null, true);

    // View preference: 'board' or 'list'
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem(VIEW_STORAGE_KEY);
            if (saved === 'board' || saved === 'list') return saved;
        } catch { }
        return 'list';
    });
    const isCompact = !screens.md;

    const handleViewChange = useCallback((value) => {
        setViewMode(value);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, value);
        } catch { }
    }, []);

    const debouncedSearch = useCallback(
        debounce((searchValue) => {
            setDebouncedSearchText(searchValue);
        }, 500),
        []
    );

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedSearch(value);
    }, [debouncedSearch]);

    const currentFilters = viewMode === 'list' ? listFilters : boardFilters;
    const currentNormalizedFilters = viewMode === 'list' ? normalizedListFilters : normalizedBoardFilters;

    const handleFiltersChange = useCallback((nextFilters, normalized) => {
        if (viewMode === 'list') {
            setListFilters(nextFilters);
            setNormalizedListFilters(normalized);
        } else {
            setBoardFilters(nextFilters);
            setNormalizedBoardFilters(normalized);
        }
    }, [viewMode]);

    const handleSortChange = useCallback(({ sort_by, sort_direction }) => {
        setSortBy(sort_by || 'created_at');
        setSortDirection(sort_by ? (sort_direction || 'desc') : 'desc');
    }, []);

    const handleTicketCreate = useCallback(async (ticketData, finish) => {
        try {
            setLoading(true);
            const payload = await createTicket(ticketData);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            setRefreshKey(k => k + 1);
            setIsModalVisible(false);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
            if (finish) finish();
        }
    }, [setLoading, handleError, t]);

    const handleClose = () => {
        setIsModalVisible(false);
    };

    return (
        <Flex
            vertical
            style={{
                flexGrow: 1,
                padding: 0,
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Toolbar */}
            <Card
                style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', flexShrink: 0 }}
                title={t('tickets')}
                extra={
                    <Space size={4}>
                        {/* View toggle */}
                        <Segmented
                            size="small"
                            value={viewMode}
                            onChange={handleViewChange}
                            options={[
                                {
                                    value: 'board',
                                    icon: <AppstoreOutlined />,
                                },
                                {
                                    value: 'list',
                                    icon: <UnorderedListOutlined />,
                                },
                            ]}
                        />
                        <Button
                            type="link"
                            icon={<PlusOutlined />}
                            size="medium"
                            onClick={() => setIsModalVisible(true)}
                        >
                            {!screens.xs && t('create_ticket')}
                        </Button>
                        <Popover
                            trigger="click"
                            placement="bottomRight"
                            open={isMoreActionsOpen}
                            onOpenChange={setIsMoreActionsOpen}
                            content={
                                <Space direction="vertical" style={{ minWidth: 140 }} onClickCapture={() => setIsMoreActionsOpen(false)}>
                                    <ImportButton resource={Resource.TICKET} type="text" size="small" />
                                    <ExportButton resource={Resource.TICKET} filters={currentNormalizedFilters} type="text" size="small" />
                                </Space>
                            }
                        >
                            <Tooltip title={t('more_actions')}>
                                <Button type="text" icon={<EllipsisOutlined />} size="medium" />
                            </Tooltip>
                        </Popover>
                    </Space>
                }
                styles={{ body: { padding: 12 } }}
            >
                <Flex gap="middle" wrap="wrap" align="center" justify="space-between">
                    <Flex flex={{ xs: '100%', md: 1 }} style={{ flexGrow: 1 }}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_tickets')}
                            size="medium"
                            onChange={handleSearchChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            value={searchText}
                            style={{
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : '400px'),
                                transition: 'width 0.4s ease-in-out'
                            }}
                        />
                    </Flex>

                    <SharedFilterBar
                        fields={filterFields}
                        value={currentFilters}
                        onChange={handleFiltersChange}
                        sortFields={[
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                            { value: 'priority', labelKey: 'ticket_priority' },
                            { value: 'category', labelKey: 'ticket_category' },
                        ]}
                        sortValue={{ sort_by: sortBy, sort_direction: sortDirection }}
                        onSortChange={handleSortChange}
                    />
                </Flex>
            </Card>

            {/* Content: Board or List */}
            <div style={{ flex: 1, marginTop: 12, minHeight: 0, overflow: 'hidden' }}>
                {viewMode === 'board' ? (
                    <KanbanBoard
                        search={debouncedSearchText}
                        filters={normalizedBoardFilters}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        refreshKey={refreshKey}
                    />
                ) : (
                    <div style={{ height: '100%', overflow: 'auto', padding: isCompact ? 0 : '0 4px' }}>
                        {isCompact ? (
                            <TicketTableView
                                search={debouncedSearchText}
                                filters={normalizedListFilters}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                refreshKey={refreshKey}
                            />
                        ) : (
                            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', height: '100%', minHeight: 400 }}>
                                <TicketTableView
                                    search={debouncedSearchText}
                                    filters={normalizedListFilters}
                                    sortBy={sortBy}
                                    sortDirection={sortDirection}
                                    refreshKey={refreshKey}
                                />
                            </Card>
                        )}
                    </div>
                )}
            </div>

            {viewTicketId && (
                <TicketDetailModal
                    ticketId={viewTicketId}
                    onClose={handleCloseTicketModal}
                />
            )}
            {isModalVisible && (
                <CreateTicketModal
                    onClose={handleClose}
                    onSubmit={handleTicketCreate}
                />
            )}
        </Flex>
    );
};

export default TicketsPage;
